import { useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ContinueButton } from '@applyft-web/ui-components';
import { EVENTS as E } from '../../core/constants';
import {
  useCustomNavigate,
  useDelayedExecute,
  useNextPageName,
  useSendEvents,
} from '../../core/hooks';
import { setTimerSeconds } from '../../core/store/app';
import {
  selectShowSuccessPopup,
  selectSuccess,
  setShowSuccessPopup,
} from '../../core/store/checkout';
import { selectEventsData } from '../../core/store/events';
import { SuccessIcon } from '../icons';
import * as S from './styled';

interface CheckoutSuccessPopupProps {
  screenId: string;
}

export const CheckoutSuccessPopup = ({ screenId }: CheckoutSuccessPopupProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useCustomNavigate();
  const nextPage = useNextPageName();
  const delayedExecute = useDelayedExecute();
  const sendEvents = useSendEvents({ screenId });
  const eventsData = useSelector(selectEventsData);
  const isSuccess = useSelector(selectSuccess);
  const showSuccessPopup = useSelector(selectShowSuccessPopup);

  const handleSuccessContinue = () => {
    sendEvents(`${E.POPUP_EVENT}__click`, { [E.BC]: 'continue', ...eventsData });
    dispatch(setShowSuccessPopup(false));
    dispatch(setTimerSeconds(null));
    navigate(nextPage);
  };

  useLayoutEffect(() => {
    if (isSuccess) {
      delayedExecute(() => dispatch(setShowSuccessPopup(true)), 100);
    }
  }, [isSuccess, dispatch, delayedExecute]);

  useLayoutEffect(() => {
    if (showSuccessPopup) {
      sendEvents(`${E.POPUP_EVENT}__screen__load`, { [E.SL]: 'success' });
    }
    // Sent once each time the popup is shown; sendEvents is recreated on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuccessPopup]);

  if (!showSuccessPopup) return null;

  return (
    <>
      <S.Overlay $isVisible />
      <S.SuccessPopupWrapper>
        <SuccessIcon />
        <S.SuccessText>{t('success_payment')}</S.SuccessText>
        <S.SuccessSubText>{t('payment_received')}</S.SuccessSubText>
        <ContinueButton
          mt={32}
          staticPosition
          id="success-popup-continue"
          onClick={handleSuccessContinue}
        >
          {t('continue')}
        </ContinueButton>
      </S.SuccessPopupWrapper>
    </>
  );
};
